import { formatUrl } from '@bulknutrients/bulk-shared-lib/utils';

import { ScrollLink } from '@components/Typography/TextLink/ScrollLink';
import {
  RewardsLink,
  TextLink,
} from '@components/Typography/TextLink/TextLink';
import type { PortableTextMarkComponentProps } from '@portabletext/react';
import { Children, isValidElement, type ReactNode } from 'react';

interface LinkLikeNodeProps {
  href?: unknown;
  children?: ReactNode;
}

function hasLinkLikeNode(node: ReactNode): boolean {
  return Children.toArray(node).some((child) => {
    if (!isValidElement<LinkLikeNodeProps>(child)) {
      return false;
    }

    if (child.type === 'a') {
      return true;
    }

    if ('href' in child.props) {
      return true;
    }

    return hasLinkLikeNode(child.props.children);
  });
}

export function LinkRenderer(
  props: PortableTextMarkComponentProps<{
    _type: string;
    href: string;
    newTab?: boolean;
    noFollow?: boolean;
  }>
) {
  if (hasLinkLikeNode(props.children)) {
    return <>{props.children}</>;
  }

  // On page link, scroll to it
  if (props.value?.href[0] === '#') {
    return (
      <ScrollLink id={props.value.href.slice(1)}>{props.children}</ScrollLink>
    );
  }

  // External link
  if (props.value?.href.startsWith('http')) {
    return (
      <TextLink
        href={props.value.href}
        target={props.value.newTab ? '_blank' : ''}
        rel={`noopener noreferrer ${props.value.noFollow ? 'nofollow' : ''}`}
      >
        {props.children}
      </TextLink>
    );
  }

  // Mail/phone link
  if (
    props.value?.href.startsWith('mailto') ||
    props.value?.href.startsWith('tel')
  ) {
    return <TextLink href={props.value.href}>{props.children}</TextLink>;
  }

  // Rewards link
  if (props.value?.href.startsWith('/rewards')) {
    return <RewardsLink>{props.children}</RewardsLink>;
  }

  // Regular next router link
  return props.value ? (
    <TextLink
      href={formatUrl(props.value.href)}
      target={props.value.newTab ? '_blank' : ''}
      rel={props.value.noFollow ? 'nofollow' : ''}
    >
      {props.children}
    </TextLink>
  ) : null;
}

export function InternalLinkRenderer(
  props: PortableTextMarkComponentProps<{
    _type: string;
    slug: {
      current: string;
    };
    newTab?: boolean;
    noFollow?: boolean;
  }>
) {
  if (hasLinkLikeNode(props.children)) {
    return <>{props.children}</>;
  }

  if (props.value?.slug?.current) {
    const rel = [] as string[];
    if (props.value.noFollow) {
      rel.push('nofollow');
    }
    if (props.value.newTab) {
      rel.push('noopener');
      rel.push('noreferrer');
    }

    return (
      <TextLink
        href={formatUrl(props.value.slug.current)}
        target={props.value.newTab ? '_blank' : ''}
        rel={rel.join(' ')}
      >
        {props.children}
      </TextLink>
    );
  }

  return (
    <>
      <span>missing link</span>
    </>
  );
}

export function OnPageLinkRenderer(
  props: PortableTextMarkComponentProps<{
    _type: string;
    id: string;
  }>
) {
  return <span id={props.value?.id}>{props.children}</span>;
}

export function LinkRendererDarkBg(
  props: PortableTextMarkComponentProps<{
    _type: string;
    href: string;
    newTab?: boolean;
    noFollow?: boolean;
  }>
) {
  if (hasLinkLikeNode(props.children)) {
    return <>{props.children}</>;
  }

  // On page link, scroll to it
  if (props.value?.href[0] === '#') {
    return (
      <ScrollLink
        id={props.value.href.slice(1)}
        className="text-white hover:text-grey-mid"
      >
        {props.children}
      </ScrollLink>
    );
  }

  // External link
  if (props.value?.href.startsWith('http')) {
    return (
      <TextLink
        href={props.value.href}
        target={props.value.newTab ? '_blank' : ''}
        rel={`noopener noreferrer ${props.value.noFollow ? 'nofollow' : ''}`}
        className="text-white hover:text-grey-mid"
      >
        {props.children}
      </TextLink>
    );
  }

  // Mail/phone link
  if (
    props.value?.href.startsWith('mailto') ||
    props.value?.href.startsWith('tel')
  ) {
    return (
      <TextLink
        href={props.value.href}
        className="text-white hover:text-grey-mid"
      >
        {props.children}
      </TextLink>
    );
  }

  // Rewards link
  if (props.value?.href.startsWith('/rewards')) {
    return (
      <RewardsLink className="text-white hover:text-grey-mid">
        {props.children}
      </RewardsLink>
    );
  }

  // Regular next router link
  return props.value ? (
    <TextLink
      href={formatUrl(props.value.href)}
      target={props.value.newTab ? '_blank' : ''}
      rel={props.value.noFollow ? 'nofollow' : ''}
      className="text-white hover:text-grey-mid"
    >
      {props.children}
    </TextLink>
  ) : null;
}

export function InternalLinkRendererDarkBg(
  props: PortableTextMarkComponentProps<{
    _type: string;
    slug: {
      current: string;
    };
    newTab?: boolean;
    noFollow?: boolean;
  }>
) {
  if (hasLinkLikeNode(props.children)) {
    return <>{props.children}</>;
  }

  if (props.value?.slug?.current) {
    const rel = [] as string[];
    if (props.value.noFollow) {
      rel.push('nofollow');
    }
    if (props.value.newTab) {
      rel.push('noopener');
      rel.push('noreferrer');
    }

    return (
      <TextLink
        href={formatUrl(props.value.slug.current)}
        target={props.value.newTab ? '_blank' : ''}
        rel={rel.join(' ')}
        className="text-white hover:text-grey-mid"
      >
        {props.children}
      </TextLink>
    );
  }

  return <span>missing link</span>;
}
